import Userback, {UserbackWidget} from '@userback/widget';
import React, {createContext, useContext, useEffect, useRef, useState} from 'react';
import {useTolgee} from 'tolgee';

import {deploymentEnv} from '~utils/deploymentEnv';
import {selectCurrentProjectId, selectUser, selectUserEmail, selectUsername} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

const UserbackContext = createContext<UserbackWidget | null>(null);

const token = import.meta.env.VITE_USERBACK_TOKEN;

const isE2eBuild = import.meta.env.VITE_E2E === 'true';

const environment = deploymentEnv || 'local';

export const UserbackProvider = ({children}: {children: React.ReactNode}) => {
  const [userback, setUserback] = useState<UserbackWidget | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const initializationAttempted = useRef(false);

  const user = useAppSelector(selectUser);
  const userName = useAppSelector(selectUsername);
  const userEmail = useAppSelector(selectUserEmail);
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const tolgee = useTolgee();

  const currentLanguageId = tolgee.getLanguage()?.substring(0, 2);

  useEffect(() => {
    if (isE2eBuild) {
      return;
    }

    const init = async () => {
      if (user?.userId) {
        initializationAttempted.current = false;
      }

      if (user && userEmail && !isInitializing && !initializationAttempted.current) {
        setIsInitializing(true);
        initializationAttempted.current = true;

        try {
          if (userback) {
            userback.destroy?.();
            setUserback(null);
          }

          if (!token || token.length < 10) {
            throw new Error('Invalid Userback token');
          }

          const instance = await Userback(token, {
            user_data: {
              id: user.userId,
              info: {
                name: userName || userEmail.split('@')[0],
                email: userEmail,
                customer: user.customer,
                projectId: currentProjectId,
                environment,
              },
            },
            custom_data: {
              environment,
            },
            autohide: true,
            native_screenshot: true,
            widget_settings: {
              language: currentLanguageId as 'en' | 'de',
            },
          });

          const fallbackName = userName || userEmail.split('@')[0];
          const userId = user.userId ?? userEmail;
          instance.identify?.(`${userId}`, {
            name: fallbackName,
            email: userEmail,
            customer: user.customer,
            projectId: currentProjectId,
            environment,
          });
          instance.setEmail?.(userEmail);
          instance.setName?.(fallbackName);
          setUserback(instance);
        } catch (error) {
          setUserback(null);
          initializationAttempted.current = false;
        } finally {
          setIsInitializing(false);
        }
      } else if (!user || !userEmail) {
        if (userback) {
          userback.destroy?.();
          setUserback(null);
        }
        initializationAttempted.current = false;
      }
    };

    init();
    // excluding 'userback' and 'isInitializing' to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userName, userEmail, currentProjectId, currentLanguageId]);

  return <UserbackContext.Provider value={userback}>{children}</UserbackContext.Provider>;
};

export const useUserback = () => useContext(UserbackContext);
